'use client';

import React, { useCallback } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

export interface PulseToggleOption<T extends string = string> {
  label: string;
  value: T;
  icon?: IconDefinition;
  disabled?: boolean;
  disabledReason?: string;
}

export interface PulseToggleProps<T extends string = string> {
  value: T;
  options: PulseToggleOption<T>[];
  onChange: (value: T) => void;
  isDisabled?: (value: T) => boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
  'data-testid'?: string;
  'aria-label'?: string;
  alwaysShowLabels?: boolean;
}

const sizeClasses = {
  xs: { container: 'h-6', text: 'text-[10px]', icon: 'text-[10px]' },
  sm: { container: 'h-8', text: 'text-xs', icon: 'text-xs' },
  md: { container: 'h-9', text: 'text-sm', icon: 'text-sm' },
  lg: { container: 'h-10', text: 'text-base', icon: 'text-base' },
};

function PulseToggle<T extends string = string>({
  value,
  options,
  onChange,
  isDisabled,
  size = 'md',
  className = '',
  'data-testid': dataTestId = 'pulse-toggle',
  'aria-label': ariaLabel,
  alwaysShowLabels = false,
}: PulseToggleProps<T>): React.ReactElement {
  const handleClick = useCallback(
    (newValue: T) => {
      if (isDisabled?.(newValue)) return;
      onChange(newValue);
    },
    [isDisabled, onChange]
  );

  const sizeConfig = sizeClasses[size];

  return (
    <div
      className={`@container/toggle relative z-[1] rounded-full border border-neutral-200 inline-flex justify-center items-center bg-white ${sizeConfig.container} ${className}`}
      data-testid={`${dataTestId}-container`}
      role="group"
      aria-label={ariaLabel}
    >
      <div
        className={`absolute h-full transition-all duration-300 ease-in-out rounded-full bg-sky-600 w-1/2 ${
          value === options[0].value ? 'left-0' : 'left-1/2'
        }`}
      />

      {options.map((option, idx) => {
        const isActive = value === option.value;
        const isOptionDisabled = option.disabled || isDisabled?.(option.value);

        return (
          <div
            key={`${option.value}-${idx}`}
            className="w-1/2 rounded-full flex justify-center items-center z-10 h-full relative px-2 @[200px]/toggle:px-3 @[250px]/toggle:px-5"
            data-testid={`${dataTestId}-${option.value}-option-container`}
          >
            <button
              type="button"
              data-testid={`${dataTestId}-${option.value}-button`}
              className={`w-full h-full rounded-full flex justify-center items-center gap-2 focus:outline-none ${
                isOptionDisabled
                  ? 'opacity-50 cursor-not-allowed'
                  : 'cursor-pointer'
              }`}
              aria-pressed={isActive}
              onClick={() => handleClick(option.value)}
              tabIndex={isOptionDisabled ? -1 : 0}
              disabled={isOptionDisabled}
              title={
                isOptionDisabled && option.disabledReason
                  ? option.disabledReason
                  : undefined
              }
            >
              <div
                className={`${
                  isActive
                    ? 'text-white font-bold'
                    : 'text-secondary font-normal'
                } ${
                  sizeConfig.text
                } font-['Inter'] leading-tight transition-all duration-300 flex items-center gap-2`}
              >
                {option.icon && (
                  <Icon
                    icon={option.icon}
                    className={`${sizeConfig.icon} transition-transform duration-300`}
                  />
                )}
                {/* Multi-word labels ("Payer Type") must stay on one line —
                    the pill is a fixed height, so wrapping clips the text. */}
                <span
                  className={`whitespace-nowrap ${
                    alwaysShowLabels
                      ? 'inline'
                      : 'hidden @[200px]/toggle:inline'
                  }`}
                >
                  {option.label}
                </span>
              </div>
            </button>
          </div>
        );
      })}
    </div>
  );
}

export default PulseToggle;
